import React, { useState, useRef, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { InfoCircleOutlined } from '@ant-design/icons';
import { Alert, Breadcrumb, Col, Row } from 'antd';

import Product from 'classes/Product';
import AlertComponent from 'components/Alert';
import { EProductStatus } from 'helpers/enums';
import type IProduct from 'interfaces/IProduct';
import PhysicalDeliveryMethods from 'scenes/Producer/Product/components/PhysicalDeliveryMethods';
import PDCorreiosValidations from 'scenes/Producer/Product/components/PhysicalDeliveryMethods/Form/Correios/validations';
import PDFixedValueValidations from 'scenes/Producer/Product/components/PhysicalDeliveryMethods/Form/FixedValue/validations';
import PDFreeValidations from 'scenes/Producer/Product/components/PhysicalDeliveryMethods/Form/Free/validations';
import General from 'scenes/Producer/Product/NewProduct/components/Forms/General';
import GeneralValidations from 'scenes/Producer/Product/NewProduct/components/Forms/General/validations';
import Sales from 'scenes/Producer/Product/NewProduct/components/Forms/Sales';
import SalesSubscriptionValidations from 'scenes/Producer/Product/NewProduct/components/Forms/Sales/components/Subscription/validations';
import SalesValidations from 'scenes/Producer/Product/NewProduct/components/Forms/Sales/validations';
import RecoverySalesValidation from 'scenes/Producer/Product/NewProduct/components/Forms/SalesRecovery/validations';
import TopPage from 'v2/components/Shared/TopPage';
import { getProductTypeTitle } from 'v2/helpers/productTypeTitle';
import { useCategories } from 'v2/hooks/products/useCategories';
import { useValidateProductTitle } from 'v2/hooks/products/useValidateProductTitle';
import { usePostMessage } from 'v2/hooks/usePostMessage';
import { PostMessageAction, PostMessageSource } from 'v2/interfaces/post-message';
import chatBotService, { Activity } from 'v2/services/chatBot';
import productService from 'v2/services/product';
import { usePhysicalDeliveryStore } from 'v2/store/PhysicalDelivery';
import { run } from 'validations/ruleRunner';

import DraftButtons from '../../components/DraftButtons';
import { NewProductHint } from '../../components/Hints/NewProductHint';
import SalesRecovery from '../components/Forms/SalesRecovery';
import { useWizardState } from '../contexts/WizardStateContext';
import { scrollToInvalidField } from '../helpers/scrollToInvalidField';

function PhysicalProduct() {
  const history = useHistory();
  const { previousStep, updateLoading, resetLoading, setProduct: setWizardProduct, nextStep } = useWizardState();
  const { delivery: physicalDelivery } = usePhysicalDeliveryStore();
  const { categories } = useCategories();
  const { productTitleExists, validateTitle: validateTitleForSave } = useValidateProductTitle();
  const sendPostMessage = usePostMessage();

  const [product, setProduct] = useState<IProduct>(() => new Product({ type: 3, sales_recovery: true }));
  const [enabledValidation, setEnabledValidation] = useState(false);

  const errorsRef = useRef<any>({});
  const fieldsRef = useRef<any>({});

  const backWizard = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      previousStep();
    },
    [previousStep]
  );

  const scrollToInvalidFields = useCallback(
    (
      generalErrors: any,
      generalRequiredFields: number,
      salesRequiredFields: number,
      deliveryRequiredFields: number,
      shouldScroll: boolean = true
    ) => {
      if (!shouldScroll) {
        return;
      }

      if (generalRequiredFields > 0) {
        scrollToInvalidField(fieldsRef.current[Object.keys(generalErrors)[0]]);
        return;
      }

      if (salesRequiredFields > 0) {
        scrollToInvalidField(fieldsRef.current['sales']);
        return;
      }

      if (deliveryRequiredFields) {
        scrollToInvalidField(fieldsRef.current['deliveries']);
      }
    },
    []
  );

  const isFormValid = useCallback(
    (productToValidate: IProduct, scroll: boolean = true): boolean => {
      const GeneralErrors = run(productToValidate, GeneralValidations);
      const generalRequiredFields = GeneralErrors ? Object.keys(GeneralErrors).length : 0;
      let salesRequiredFields = 0;

      if (productToValidate?.charge_type === 'N') {
        const e = run(productToValidate, SalesValidations);
        salesRequiredFields += e ? Object.keys(e).length : 0;
      }

      if (productToValidate?.charge_type === 'A') {
        const e = run(productToValidate, SalesSubscriptionValidations);
        salesRequiredFields += e ? Object.keys(e).length : 0;
      }

      if (productToValidate?.sales_recovery) {
        const e = run(productToValidate, RecoverySalesValidation);
        salesRequiredFields += e ? Object.keys(e).length : 0;
      }

      let deliveryRequiredFields = 0;
      const deliveryType = String(productToValidate.physicalDelivery?.delivery_type ?? '');

      if (deliveryType === '3') {
        const e = run(productToValidate.physicalDelivery, PDCorreiosValidations);
        deliveryRequiredFields += e ? Object.keys(e).length : 0;
      } else if (deliveryType === '2') {
        const e = run(productToValidate.physicalDelivery, PDFixedValueValidations);
        deliveryRequiredFields += e ? Object.keys(e).length : 0;
      } else if (deliveryType === '1') {
        const e = run(productToValidate.physicalDelivery, PDFreeValidations);
        deliveryRequiredFields += e ? Object.keys(e).length : 0;
      }

      scrollToInvalidFields(GeneralErrors, generalRequiredFields, salesRequiredFields, deliveryRequiredFields, scroll);
      return generalRequiredFields === 0 && salesRequiredFields === 0 && deliveryRequiredFields === 0;
    },
    [scrollToInvalidFields]
  );

  const handleSubmit = useCallback(
    async (isDraft: boolean = false) => {
      const productToSubmit = new Product(product);
      productToSubmit.physicalDelivery = physicalDelivery;

      if (isDraft) {
        productToSubmit.active = EProductStatus.DRAFT;
      }

      resetLoading();

      if (!isDraft && !isFormValid(productToSubmit)) {
        setEnabledValidation(true);
        toast.error('Por favor preencha todos os campos obrigatórios');
        return;
      }

      (async () => {
        try {
          updateLoading(33);
          const createdData = await productService.createProduct(productToSubmit.serviceFormat());
          const prod = createdData?.product ?? createdData;

          if (!prod?.id) {
            toast.error('Erro ao criar o produto');
            resetLoading();
            return;
          }

          if (isDraft) {
            sendPostMessage({
              source: PostMessageSource.PRODUCT_MANAGER,
              action: PostMessageAction.PRODUCT_CREATED,
              data: {
                product_id: prod.id,
                is_draft: true,
                completed_at: new Date().toISOString(),
                product_type: productToSubmit.type,
                product_type_title: getProductTypeTitle(productToSubmit.type)
              }
            });
            toast.success('Seu produto foi salvo como rascunho!');
            history.push('/producer/product/myproducts');
            return;
          }

          updateLoading(66);
          const loadResult: any = await productService.loadProductById(prod.id, false);
          const newProd = new Product(loadResult?.data ?? loadResult);
          newProd.setDeliveries(productToSubmit.deliveries);
          setWizardProduct(newProd);
          updateLoading(100);
          resetLoading();
          nextStep();
        } catch (error) {
          toast.error(error);
          setProduct(productToSubmit);
          resetLoading();
        }

        chatBotService.activity(Activity.productRegistered);
      })();
    },
    [
      product,
      isFormValid,
      history,
      physicalDelivery,
      resetLoading,
      updateLoading,
      setWizardProduct,
      nextStep,
      sendPostMessage
    ]
  );

  const handleClickSubmitDraft = useCallback(async () => {
    const confirm = await AlertComponent.show({
      title: 'Quase lá...',
      caption: 'Seu produto será salvo como rascunho. Para publicar, volte a editar esse produto.',
      message: 'O seu produto não poderá ser vendido enquanto você não publicá-lo para venda. Deseja Continuar?',
      confirmation: true,
      buttonOk: 'Confirmar e salvar',
      size: 'md'
    });

    if (confirm) {
      handleSubmit(true);
    }
  }, [handleSubmit]);

  const handleClickSubmit = useCallback(async () => {
    const productToValidate = new Product(product);
    productToValidate.physicalDelivery = physicalDelivery;

    if (!isFormValid(productToValidate)) {
      setEnabledValidation(true);
      toast.error('Por favor preencha todos os campos obrigatórios');
      return;
    }

    const message = (
      <>
        Concordo que meu produto está em conformidade com as recomendações para evitar interrupções nas vendas durante o
        processo de análise e moderação.
        <Alert
          icon={<InfoCircleOutlined style={{ fontSize: 24 }} />}
          type='info'
          className='mt-3'
          message={
            <>
              Caso seja reprovado na moderação, seu produto ficará indisponível para venda.{' '}
              <a
                href='https://ajuda.eduzz.com/hc/pt-br/articles/1260807655450-Como-funciona-a-modera%C3%A7%C3%A3o-de-conte%C3%BAdo'
                rel='noopener noreferrer'
                target='_blank'
              >
                Saiba mais
              </a>
            </>
          }
        />
      </>
    );
    const confirm = await AlertComponent.show({
      title: 'Quase lá...',
      caption: 'Seu produto será enviado para a moderação, mas não se preocupe, você já pode começar a vender.',
      message,
      confirmation: true,
      buttonOk: 'Confirmar e cadastrar',
      size: 'md'
    });

    if (confirm) {
      handleSubmit();
    }
  }, [handleSubmit, isFormValid, product, physicalDelivery]);

  const setRefField = useCallback((name: any, ref: any) => {
    fieldsRef.current[name] = ref;
  }, []);
  const setRefError = useCallback((name: any, ref: any) => {
    errorsRef.current[name] = ref;
  }, []);
  const handleChange = useCallback((name: string, value: any) => {
    setProduct(prev => {
      const next = new Product(prev);
      (next as Record<string, any>)[name] = value;
      return next;
    });
  }, []);

  const canSaveDraft = !!product.title;

  return (
    <Row id='new-physicalproduct' gutter={[16, 16]}>
      <Col span={24}>
        <Breadcrumb
          items={[
            { title: <Link to='/producer/product/myproducts'>Meus Produtos</Link> },
            {
              title: (
                <a href='#' style={{ cursor: 'pointer' }} onClick={backWizard}>
                  Tipo de produto
                </a>
              )
            },
            { title: 'Novo produto' }
          ]}
        />
      </Col>
      <Col span={24}>
        <TopPage
          title='Novo Produto Físico'
          helpLink='https://ajuda.eduzz.com/hc/pt-br/articles/4402880123931-Como-cadastrar-um-produto-f%C3%ADsico-%C3%93rbita'
        />
      </Col>

      <Col md={18} xs={24}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <General
              setRefField={setRefField}
              setRefError={setRefError}
              product={product}
              handleChange={handleChange}
              categoryList={categories}
              validateTitleForSave={validateTitleForSave}
              productTitleExists={productTitleExists}
              enabledValidation={enabledValidation}
            />
          </Col>
          <Col span={24}>
            <Sales
              ref={(ref: any) => setRefField('sales', ref)}
              product={product}
              handleChange={handleChange}
              setRefField={setRefField}
              setRefError={setRefError}
              enabledValidation={enabledValidation}
            />
          </Col>
          <Col span={24}>
            <SalesRecovery handleChange={handleChange} product={product} enabledValidation={enabledValidation} />
          </Col>
          <Col span={24}>
            <PhysicalDeliveryMethods
              ref={(ref: any) => setRefField('deliveries', ref)}
              fade={true}
              setRefField={setRefField}
              enabledValidation={enabledValidation}
              product={product}
            />
          </Col>
          <Col span={24}>
            <DraftButtons
              disabled={{ tooltip: false, button: false }}
              disabledDraft={{ tooltip: !!canSaveDraft, button: !canSaveDraft }}
              onSubmit={() => handleClickSubmit()}
              onSubmitDraft={() => handleClickSubmitDraft()}
              isService={false}
            />
          </Col>
        </Row>
      </Col>
      <Col md={6} xs={24}>
        <NewProductHint type='physical' />
      </Col>
    </Row>
  );
}

export default PhysicalProduct;
